// One document, from whichever of the two read paths is serving this session
// (#207 U5): `settings/studio`, `settings/onboarding` and `billing/account`.
//
// ── *Never told* is not *told it is absent* (#207's failure 1) ───────────
//
// `useSettings` treats an absent document as **read**, and
// `normaliseStudio(null)` defaults `locale` to `en-US`, from which
// `src/domain/locale.js` derives the currency. A read path that cannot tell
// *we have never been told* from *we were told it is absent* would change
// every amount in a British studio on a cold offline open (see `appStore.js`).
// The store keeps those apart and this hook must not collapse them. **Neither
// answer is `data !== null`** — `exists: false` with a receipt is an answer.
//
// ── Two questions, two names (#279) ──────────────────────────────────────
//
// Callers ask two different things of a document read, and neither is
// derivable from the other:
//
//   `hasRead`    **the server has answered for this scope.** A receipt exists:
//                `stale` (a previous session's, and still a true statement
//                about what the studio holds) or `fresh`, as
//                `useCollectionWindow.hasRead` means. It is the gate on a
//                write that would *create* something, because creating
//                against a document nobody has read is how a studio gets a
//                second copy of what it has.
//
//   `hasAnswer`  **there is something to show.** `!documentIsLoading` (#267)
//                with the pre-first-read placeholder counted in (#279): a
//                receipt, or a queued write this session that put a document on
//                screen with nothing stored behind it. This is the gate on
//                *rendering*, and on a save that merges into what is already
//                there — `useSettings` is the caller, and `loading` is its
//                negation.
//
// A queued creation with no receipt is `hasAnswer` without `hasRead`; a
// receipt whose window says the document is absent is both; a first read that
// has not come back is neither (#279). Each name means one thing on both window
// hooks, and a caller says which.
//
// ── Everything else is `useCollectionWindow`'s ───────────────────────────
//
// The flag is read in one place, the snapshot path is the rollback,
// `pendingWrite` on the store path comes from our queue rather than the SDK's
// metadata, and a stale read is marked and never blocked (#207 Q5). The store
// path asks for metadata changes (#682), for `fromCache` alone — see
// `subscribe`.

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { onSnapshot } from 'firebase/firestore';
import { docRef } from './paths';
import { READ_STATE, documentScope } from './appStore';
import { forgetRead, reportRead } from './staleReads';
import { isConfigured } from '../lib/firebase';
import { theReadPath } from './readStore';
import { readStoreEnabled } from './readStoreFlag';
import { useStoredDocument } from './useStoredDocument';
import { onRetryReads } from './readRetry';

/** The snapshot's `fromCache`, false when it does not say (a test's stand-in). */
const cachedSnapshot = (snap) => Boolean(snap && snap.metadata && snap.metadata.fromCache === true);

/**
 * @param uid         the studio, or falsy for signed out.
 * @param collection  a `COLLECTIONS` name.
 * @param id          the document id under it.
 *
 * Returns `{ data, exists, hasRead, hasAnswer, loading, readVia, readState,
 * readSeenAt, pendingWrite, partial, error }`.
 *
 * **`data` is `null` both for "never heard" and for "the server says it is not
 * there".** `hasAnswer` is what tells them apart, and `hasRead` answers the
 * other question (see the header). `loading` is `!hasAnswer` narrowed to a
 * signed-in studio, because signed out is not loading: there is nothing to
 * read.
 *
 * **`exists` is a third question, the one a writer asks** (#337): *is there a
 * document at this path to merge into, or would this write create one?* It is
 * `useStoredDocument`'s own field on the store path, folded over the queue, so
 * a creation this device has queued and the server has never seen counts as
 * existing.
 *
 * It is **not** an answer on its own: `exists: false` with `hasAnswer` false is
 * "we have not heard", which is no more a statement that the document is absent
 * than `data: null` is. A caller deciding to create reads both, and
 * `useSettings.saveStudio` is the worked example.
 */
export function useDocumentWindow({ uid, collection, id }) {
  const [viaStore] = useState(() => readStoreEnabled());
  const readPath = useMemo(() => (viaStore ? theReadPath() : null), [viaStore]);

  const studio = (isConfigured && uid) || null;

  // Try again (#647), as `useCollectionWindow` does it.
  const [attempt, setAttempt] = useState(0);
  const failedRef = useRef(false);
  useEffect(() => onRetryReads(() => {
    if (failedRef.current) setAttempt((a) => a + 1);
  }), []);

  // With metadata changes (#682), so the store hears the moment the server
  // first answers: a document the SDK's cache already held arrives again,
  // unchanged, with `fromCache` false, and Firestore raises that only to a
  // listener that asked. Every snapshot goes on rather than a filtered few — a
  // document has no `docChanges` to tell a data change from a metadata one —
  // at the cost of a re-store of one document on a pending-write flip.
  const subscribe = useCallback((onDocument, onError) => {
    if (!studio) return () => {};
    return onSnapshot(docRef(studio, collection, id), { includeMetadataChanges: true }, (snap) => {
      // `null` rather than `undefined` for a document that is not there, which
      // is what `appStore.replaceDocument` reads as `exists: false` — a stored
      // answer rather than a stored absence.
      onDocument(snap.exists() ? snap.data() : null, { fromCache: cachedSnapshot(snap) });
    }, onError);
  }, [studio, collection, id, attempt]);

  const stored = useStoredDocument({
    uid: studio, collection, id, subscribe, path: readPath, enabled: viaStore,
  });

  // `{ uid, data }` rather than a bare `data`, so that what is in hand can be
  // checked against who it belongs to: whatever arrived a moment ago belongs to
  // whoever was signed in then.
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotError, setSnapshotError] = useState(null);

  useEffect(() => {
    setSnapshotError(null);
    if (viaStore) return undefined;
    if (!studio) {
      setSnapshot(null);
      return undefined;
    }
    setSnapshot(null);
    // The server has answered this listener (#682): `fresh` on this path.
    let heard = false;
    return onSnapshot(
      docRef(studio, collection, id),
      { includeMetadataChanges: true },
      (snap) => {
        if (!cachedSnapshot(snap)) heard = true;
        setSnapshot({ uid: studio, data: snap.exists() ? snap.data() : null, heard });
      },
      (err) => {
        console.error(`[reads] listening to ${collection}/${id} failed:`, err);
        setSnapshotError({ uid: studio, err });
      },
    );
  }, [viaStore, studio, collection, id, attempt]);

  // Reported to the sync chip (#207 U6), as `useCollectionWindow` does. A
  // document scope is **not** its collection, so this reports `documentScope`
  // — `settings/studio` going stale is not `settings/onboarding` going stale.
  //
  // Both paths (#682), as `useCollectionWindow` explains: on the snapshot
  // path a listener that has answered only from the SDK's cache this session
  // is `stale`.
  const snapshotFor = snapshot && snapshot.uid === studio ? snapshot : null;
  const snapshotState = snapshotFor ? (snapshotFor.heard ? READ_STATE.fresh : READ_STATE.stale) : null;
  const reportedState = viaStore ? stored.state : snapshotState;
  useEffect(() => {
    if (!studio) return undefined;
    const scope = documentScope(studio, collection, id);
    reportRead(scope, reportedState);
    return () => forgetRead(scope);
  }, [studio, collection, id, reportedState]);

  if (viaStore) {
    // The receipt, exactly as `useCollectionWindow` reads it. `null` — the
    // first read has not come back — is neither of these and is therefore
    // false (#279).
    const hasRead = Boolean(studio)
      && (stored.state === READ_STATE.stale || stored.state === READ_STATE.fresh);
    // Something to show. `useStoredDocument` owns this answer, because it is
    // the only place that can see the difference between a read that has not
    // resolved and one that came back with nothing.
    const hasAnswer = Boolean(studio) && !stored.loading;
    // Stopped before it answered (#647).
    const failed = Boolean(studio) && !hasAnswer && Boolean(stored.failed);
    failedRef.current = failed;
    return {
      data: stored.data,
      // The store's own answer, not `data !== null`. The fold builds a `data`
      // for every document it says exists, so the two agree, but `exists` is
      // the field defined to mean this.
      exists: Boolean(stored.exists),
      hasRead,
      hasAnswer,
      loading: Boolean(studio) && !hasAnswer,
      failed,
      readVia: 'store',
      readState: stored.state,
      readSeenAt: stored.seenAt,
      pendingWrite: stored.pendingWrite,
      partial: stored.partial,
      error: stored.error,
    };
  }

  // One listener, one arrival: the snapshot path cannot separate the two
  // questions, so both names carry the same answer here.
  const hasRead = Boolean(studio) && snapshot?.uid === studio;
  const failed = Boolean(studio) && !hasRead && snapshotError?.uid === studio;
  failedRef.current = failed;
  return {
    data: hasRead ? snapshot.data : null,
    // `snap.exists()` as the listener above recorded it: `null` for a document
    // that is not there and the document's fields otherwise. Before the first
    // snapshot this is false for "we have not heard", so it is not an answer
    // without `hasAnswer` beside it.
    exists: hasRead && snapshot.data !== null,
    hasRead,
    hasAnswer: hasRead,
    loading: Boolean(studio) && !hasRead,
    failed,
    readVia: 'snapshot',
    // Not returned on this path; the sync chip's state is reported above,
    // from `fromCache`.
    readState: null,
    readSeenAt: null,
    pendingWrite: false,
    partial: false,
    error: null,
  };
}
